export interface GrammarExample {
  en: string;
  vi: string;
}

export interface GrammarTopic {
  id: string;
  categoryId: string;
  title: string;
  level: "A1" | "A2" | "B1" | "B2" | "C1";
  summary: string;
  points: string[];
  examples: GrammarExample[];
}

export interface GrammarCategory {
  id: string;
  title: string;
}

export interface GrammarExercise {
  /** Sentence with the blank written as ___ */
  question: string;
  options: string[];
  correctIndex: number;
  /** Vietnamese explanation of why the correct answer is right */
  explanationVi: string;
}

export const GRAMMAR_CATEGORIES: GrammarCategory[] = [
  { id: "tenses", title: "Thì (Tenses)" },
  { id: "verb-structures", title: "Cấu trúc động từ" },
  { id: "nouns-articles-pronouns", title: "Danh từ, mạo từ & đại từ" },
  { id: "adjectives-adverbs", title: "Tính từ & trạng từ" },
  { id: "prepositions-conjunctions", title: "Giới từ & liên từ" },
  { id: "questions-negatives", title: "Câu hỏi & câu phủ định" },
];

export const GRAMMAR_TOPICS: GrammarTopic[] = [
  // ───────────────────────── Tenses ─────────────────────────
  {
    id: "present-simple",
    categoryId: "tenses",
    title: "Present Simple",
    level: "A1",
    summary: "Diễn tả thói quen, sự thật hiển nhiên, lịch trình cố định.",
    points: [
      "Công thức khẳng định: S + V(s/es) (V nguyên thể với I/You/We/They, thêm s/es với He/She/It)",
      "Phủ định: S + do/does + not + V",
      "Nghi vấn: Do/Does + S + V?",
      "Dùng cho: thói quen, sự thật khoa học, lịch trình (tàu, giờ học), cảm xúc/suy nghĩ (like, want, believe)",
      "Từ nhận biết: always, usually, often, sometimes, rarely, never, every day/week",
    ],
    examples: [
      { en: "She works at a hospital.", vi: "Cô ấy làm việc ở bệnh viện." },
      { en: "Water boils at 100 degrees Celsius.", vi: "Nước sôi ở 100 độ C." },
      { en: "The train leaves at 7 a.m.", vi: "Tàu khởi hành lúc 7 giờ sáng." },
      { en: "I don't drink coffee at night.", vi: "Tôi không uống cà phê vào buổi tối." },
    ],
  },
  {
    id: "present-continuous",
    categoryId: "tenses",
    title: "Present Continuous",
    level: "A1",
    summary: "Diễn tả hành động đang xảy ra ngay lúc nói hoặc kế hoạch gần trong tương lai.",
    points: [
      "Công thức: S + am/is/are + V-ing",
      "Phủ định: S + am/is/are + not + V-ing",
      "Nghi vấn: Am/Is/Are + S + V-ing?",
      "Dùng cho: hành động đang diễn ra tại thời điểm nói, xu hướng đang thay đổi, kế hoạch đã sắp xếp trong tương lai gần",
      "Từ nhận biết: now, right now, at the moment, these days, Look!, Listen!",
      "Một số động từ chỉ trạng thái (know, like, want, believe, own) thường KHÔNG dùng ở thì tiếp diễn",
    ],
    examples: [
      { en: "I am writing an email right now.", vi: "Tôi đang viết email ngay lúc này." },
      { en: "They are studying English this semester.", vi: "Họ đang học tiếng Anh học kỳ này." },
      { en: "We are meeting the client tomorrow morning.", vi: "Chúng tôi sẽ gặp khách hàng vào sáng mai (đã lên lịch)." },
      { en: "She isn't listening to me.", vi: "Cô ấy không lắng nghe tôi." },
    ],
  },
  {
    id: "present-perfect",
    categoryId: "tenses",
    title: "Present Perfect",
    level: "A2",
    summary: "Nối kết quá khứ với hiện tại: kinh nghiệm, hành động vừa hoàn thành, hành động bắt đầu trong quá khứ và còn tiếp diễn.",
    points: [
      "Công thức: S + have/has + V3/-ed",
      "Phủ định: S + have/has + not + V3",
      "Nghi vấn: Have/Has + S + V3?",
      "Dùng cho: kinh nghiệm (chưa từng/đã từng), hành động vừa xảy ra ảnh hưởng đến hiện tại, hành động kéo dài từ quá khứ đến hiện tại",
      "Từ nhận biết: already, just, yet, ever, never, since, for, so far, recently",
      "Phân biệt với Past Simple: Present Perfect không nói rõ thời điểm cụ thể trong quá khứ",
    ],
    examples: [
      { en: "I have already finished the report.", vi: "Tôi đã hoàn thành báo cáo rồi." },
      { en: "Have you ever been to Japan?", vi: "Bạn đã từng đến Nhật Bản chưa?" },
      { en: "She has worked here since 2019.", vi: "Cô ấy đã làm việc ở đây từ năm 2019." },
      { en: "We haven't decided yet.", vi: "Chúng tôi chưa quyết định." },
    ],
  },
  {
    id: "present-perfect-continuous",
    categoryId: "tenses",
    title: "Present Perfect Continuous",
    level: "B1",
    summary: "Nhấn mạnh khoảng thời gian của một hành động bắt đầu trong quá khứ và kéo dài đến hiện tại (có thể vẫn tiếp diễn).",
    points: [
      "Công thức: S + have/has + been + V-ing",
      "Phủ định: S + have/has + not + been + V-ing",
      "Nghi vấn: Have/Has + S + been + V-ing?",
      "Nhấn mạnh QUÁ TRÌNH/THỜI LƯỢNG, khác với Present Perfect nhấn mạnh KẾT QUẢ",
      "Từ nhận biết: for, since, how long, lately, recently",
    ],
    examples: [
      { en: "I have been learning English for three years.", vi: "Tôi đã học tiếng Anh được ba năm." },
      { en: "She has been waiting for you since 2 p.m.", vi: "Cô ấy đã chờ bạn từ 2 giờ chiều." },
      { en: "Why are you tired? — I've been running.", vi: "Sao bạn mệt vậy? — Tôi vừa chạy bộ xong." },
    ],
  },
  {
    id: "past-simple",
    categoryId: "tenses",
    title: "Past Simple",
    level: "A1",
    summary: "Diễn tả hành động đã xảy ra và kết thúc trong quá khứ, tại một thời điểm xác định.",
    points: [
      "Công thức: S + V2/-ed",
      "Phủ định: S + did + not + V (nguyên thể)",
      "Nghi vấn: Did + S + V (nguyên thể)?",
      "Dùng cho: hành động đã hoàn tất trong quá khứ, chuỗi hành động liên tiếp trong một câu chuyện",
      "Từ nhận biết: yesterday, last week/month/year, ago, in 2010, when I was young",
    ],
    examples: [
      { en: "I visited my grandparents last weekend.", vi: "Tôi đã thăm ông bà vào cuối tuần trước." },
      { en: "She didn't call me yesterday.", vi: "Cô ấy đã không gọi cho tôi hôm qua." },
      { en: "Did you see that movie?", vi: "Bạn đã xem bộ phim đó chưa?" },
    ],
  },
  {
    id: "past-continuous",
    categoryId: "tenses",
    title: "Past Continuous",
    level: "A2",
    summary: "Diễn tả hành động đang xảy ra tại một thời điểm cụ thể trong quá khứ, thường bị hành động khác xen vào.",
    points: [
      "Công thức: S + was/were + V-ing",
      "Thường đi kèm Past Simple để diễn tả hành động ngắn xen vào hành động đang diễn ra (when + Past Simple)",
      "Dùng cho: hai hành động song song trong quá khứ (while... was/were... V-ing)",
      "Từ nhận biết: while, when, at that time, at 8 p.m. yesterday",
    ],
    examples: [
      { en: "I was cooking dinner when he arrived.", vi: "Tôi đang nấu bữa tối thì anh ấy đến." },
      { en: "They were watching TV while I was studying.", vi: "Họ đang xem TV trong khi tôi đang học bài." },
      { en: "What were you doing at 9 p.m. last night?", vi: "Tối qua lúc 9 giờ bạn đang làm gì?" },
    ],
  },
  {
    id: "past-perfect",
    categoryId: "tenses",
    title: "Past Perfect",
    level: "B1",
    summary: "Diễn tả hành động xảy ra trước một hành động/thời điểm khác trong quá khứ.",
    points: [
      "Công thức: S + had + V3/-ed",
      "Dùng để nói hành động nào xảy ra TRƯỚC trong hai hành động quá khứ",
      "Thường dùng với: before, after, by the time, already, when",
    ],
    examples: [
      { en: "The train had already left when we arrived at the station.", vi: "Tàu đã rời đi trước khi chúng tôi đến ga." },
      { en: "She had finished her homework before her mom came home.", vi: "Cô bé đã làm xong bài tập trước khi mẹ về nhà." },
      { en: "I hadn't seen him before that party.", vi: "Tôi chưa từng gặp anh ấy trước bữa tiệc đó." },
    ],
  },
  {
    id: "past-perfect-continuous",
    categoryId: "tenses",
    title: "Past Perfect Continuous",
    level: "B2",
    summary: "Nhấn mạnh khoảng thời gian của một hành động kéo dài trước một thời điểm/hành động khác trong quá khứ.",
    points: [
      "Công thức: S + had + been + V-ing",
      "Nhấn mạnh QUÁ TRÌNH kéo dài trước một mốc thời gian trong quá khứ",
      "Thường dùng với: for, since, before",
    ],
    examples: [
      { en: "He had been working there for ten years before he retired.", vi: "Ông ấy đã làm việc ở đó mười năm trước khi nghỉ hưu." },
      { en: "They were tired because they had been driving all day.", vi: "Họ mệt vì đã lái xe suốt cả ngày." },
    ],
  },
  {
    id: "future-simple-will",
    categoryId: "tenses",
    title: "Future Simple (will)",
    level: "A1",
    summary: "Diễn tả dự đoán, quyết định tức thời, lời hứa, đề nghị.",
    points: [
      "Công thức: S + will + V (nguyên thể)",
      "Phủ định: S + will + not (won't) + V",
      "Dùng cho: dự đoán không có căn cứ rõ ràng, quyết định ngay lúc nói, lời hứa/đề nghị/yêu cầu",
      "Từ nhận biết: I think/promise/hope..., tomorrow, next week, in the future",
    ],
    examples: [
      { en: "I think it will rain tomorrow.", vi: "Tôi nghĩ ngày mai trời sẽ mưa." },
      { en: "I'll help you with that.", vi: "Tôi sẽ giúp bạn việc đó (quyết định ngay lúc nói)." },
      { en: "I promise I will call you tonight.", vi: "Tôi hứa tối nay sẽ gọi cho bạn." },
    ],
  },
  {
    id: "be-going-to",
    categoryId: "tenses",
    title: "Be going to",
    level: "A1",
    summary: "Diễn tả dự định đã có kế hoạch từ trước hoặc dự đoán dựa trên dấu hiệu hiện tại.",
    points: [
      "Công thức: S + am/is/are + going to + V (nguyên thể)",
      "Khác với 'will': 'be going to' dùng khi đã có KẾ HOẠCH hoặc có DẤU HIỆU rõ ràng ở hiện tại",
    ],
    examples: [
      { en: "I'm going to study abroad next year.", vi: "Tôi định đi du học năm sau (đã có kế hoạch)." },
      { en: "Look at those clouds! It's going to rain.", vi: "Nhìn những đám mây kia kìa! Trời sắp mưa (có dấu hiệu)." },
    ],
  },
  {
    id: "future-continuous",
    categoryId: "tenses",
    title: "Future Continuous",
    level: "B1",
    summary: "Diễn tả hành động đang diễn ra tại một thời điểm xác định trong tương lai.",
    points: [
      "Công thức: S + will + be + V-ing",
      "Dùng cho: hành động sẽ đang xảy ra tại một mốc thời gian cụ thể trong tương lai",
    ],
    examples: [
      { en: "This time next week, I will be lying on a beach.", vi: "Giờ này tuần sau, tôi sẽ đang nằm trên bãi biển." },
      { en: "She will be attending the conference all day tomorrow.", vi: "Cô ấy sẽ tham dự hội nghị cả ngày mai." },
    ],
  },
  {
    id: "future-perfect",
    categoryId: "tenses",
    title: "Future Perfect",
    level: "B2",
    summary: "Diễn tả hành động sẽ hoàn thành trước một thời điểm/mốc cụ thể trong tương lai.",
    points: [
      "Công thức: S + will + have + V3/-ed",
      "Thường dùng với: by + thời gian, by the time",
    ],
    examples: [
      { en: "By 2030, I will have finished my PhD.", vi: "Đến năm 2030, tôi sẽ hoàn thành bằng tiến sĩ." },
      { en: "By the time you arrive, we will have already eaten.", vi: "Khi bạn đến, chúng tôi sẽ đã ăn xong rồi." },
    ],
  },
  {
    id: "future-perfect-continuous",
    categoryId: "tenses",
    title: "Future Perfect Continuous",
    level: "C1",
    summary: "Nhấn mạnh khoảng thời gian của một hành động sẽ kéo dài liên tục cho đến một mốc trong tương lai.",
    points: [
      "Công thức: S + will + have + been + V-ing",
      "Thường dùng với: by, for + khoảng thời gian",
    ],
    examples: [
      { en: "By next month, I will have been working here for five years.", vi: "Đến tháng sau, tôi sẽ đã làm việc ở đây được năm năm." },
    ],
  },

  // ───────────────────────── Verb structures ─────────────────────────
  {
    id: "modal-verbs",
    categoryId: "verb-structures",
    title: "Modal Verbs",
    level: "A2",
    summary: "Động từ khuyết thiếu diễn tả khả năng, sự cho phép, nghĩa vụ, dự đoán.",
    points: [
      "can/could: khả năng, sự cho phép (could lịch sự hơn/quá khứ của can)",
      "may/might: sự cho phép trang trọng, khả năng có thể xảy ra (might chắc chắn thấp hơn may)",
      "must: sự bắt buộc/suy luận chắc chắn; mustn't = cấm",
      "have to / don't have to: nghĩa vụ từ bên ngoài / không bắt buộc",
      "should/ought to: lời khuyên",
      "will/would: dự đoán, thói quen quá khứ (would), lời mời lịch sự (would you...)",
      "Sau modal verb luôn là V nguyên thể (không chia thì, không thêm to trừ 'ought to' và 'have to')",
    ],
    examples: [
      { en: "She can speak three languages.", vi: "Cô ấy có thể nói ba ngôn ngữ." },
      { en: "You must wear a helmet.", vi: "Bạn bắt buộc phải đội mũ bảo hiểm." },
      { en: "You don't have to come if you're busy.", vi: "Bạn không bắt buộc phải đến nếu bận." },
      { en: "You should see a doctor.", vi: "Bạn nên đi khám bác sĩ." },
      { en: "It might rain later.", vi: "Có thể lát nữa trời sẽ mưa." },
    ],
  },
  {
    id: "passive-voice",
    categoryId: "verb-structures",
    title: "Passive Voice",
    level: "B1",
    summary: "Câu bị động — nhấn mạnh hành động hoặc đối tượng chịu tác động thay vì người thực hiện.",
    points: [
      "Công thức: S + be (chia theo thì) + V3/-ed (+ by + tác nhân, nếu cần)",
      "Dùng khi: người thực hiện không quan trọng/không rõ, hoặc muốn nhấn mạnh đối tượng chịu tác động",
      "Mỗi thì có dạng bị động tương ứng: is done, was done, has been done, will be done, is being done...",
    ],
    examples: [
      { en: "The report was written by the intern.", vi: "Báo cáo được viết bởi thực tập sinh." },
      { en: "English is spoken all over the world.", vi: "Tiếng Anh được nói khắp thế giới." },
      { en: "The bridge has been repaired.", vi: "Cây cầu đã được sửa xong." },
      { en: "The project will be completed next month.", vi: "Dự án sẽ được hoàn thành vào tháng sau." },
    ],
  },
  {
    id: "conditionals",
    categoryId: "verb-structures",
    title: "Conditional Sentences (Câu điều kiện)",
    level: "B1",
    summary: "Diễn tả điều kiện và kết quả — 5 loại chính: 0, 1, 2, 3 và hỗn hợp.",
    points: [
      "Loại 0 (sự thật hiển nhiên): If + S + V(s/es), S + V(s/es)",
      "Loại 1 (có thể xảy ra ở tương lai): If + S + V(s/es), S + will + V",
      "Loại 2 (giả định trái với hiện tại): If + S + V2/-ed (were với to be), S + would + V",
      "Loại 3 (giả định trái với quá khứ, hối tiếc): If + S + had + V3, S + would + have + V3",
      "Hỗn hợp: kết hợp điều kiện quá khứ với kết quả hiện tại — If + had + V3, S + would + V (nguyên thể)",
    ],
    examples: [
      { en: "If you heat ice, it melts.", vi: "Nếu bạn làm nóng đá, nó tan chảy. (loại 0)" },
      { en: "If it rains tomorrow, I will stay home.", vi: "Nếu ngày mai trời mưa, tôi sẽ ở nhà. (loại 1)" },
      { en: "If I had more money, I would travel more.", vi: "Nếu tôi có nhiều tiền hơn, tôi sẽ đi du lịch nhiều hơn. (loại 2)" },
      { en: "If she had studied harder, she would have passed the exam.", vi: "Nếu cô ấy học chăm hơn, cô ấy đã đậu kỳ thi. (loại 3)" },
      { en: "If I hadn't missed the flight, I would be there now.", vi: "Nếu tôi không lỡ chuyến bay, giờ tôi đã ở đó rồi. (hỗn hợp)" },
    ],
  },
  {
    id: "reported-speech",
    categoryId: "verb-structures",
    title: "Reported Speech (Câu tường thuật)",
    level: "B1",
    summary: "Thuật lại lời nói của người khác — thì thường lùi về một bậc so với câu trực tiếp.",
    points: [
      "Câu kể: S + said/told + (that) + S lùi thì",
      "Câu hỏi Yes/No: S + asked + if/whether + S + V (lùi thì)",
      "Câu hỏi Wh-: S + asked + Wh-word + S + V (lùi thì), đổi về câu khẳng định (không đảo ngữ)",
      "Câu mệnh lệnh: S + told/asked + O + (not) to + V",
      "Lùi thì: Present Simple→Past Simple, Present Continuous→Past Continuous, Present Perfect→Past Perfect, will→would, can→could, must→had to",
      "Đổi trạng từ chỉ thời gian/nơi chốn: now→then, today→that day, tomorrow→the next day, here→there, this→that",
    ],
    examples: [
      { en: "\"I am tired,\" she said. → She said (that) she was tired.", vi: "Cô ấy nói rằng cô ấy mệt." },
      { en: "\"Do you like coffee?\" he asked. → He asked if I liked coffee.", vi: "Anh ấy hỏi tôi có thích cà phê không." },
      { en: "\"Where do you live?\" she asked. → She asked where I lived.", vi: "Cô ấy hỏi tôi sống ở đâu." },
      { en: "\"Close the door,\" he said. → He told me to close the door.", vi: "Anh ấy bảo tôi đóng cửa lại." },
    ],
  },
  {
    id: "gerunds-infinitives",
    categoryId: "verb-structures",
    title: "Gerunds & Infinitives (V-ing vs to V)",
    level: "B1",
    summary: "Một số động từ theo sau bởi V-ing, một số theo sau bởi to V, một số theo cả hai (có thể đổi nghĩa).",
    points: [
      "V-ing theo sau: enjoy, avoid, suggest, mind, finish, consider, keep, admit, imagine",
      "to V theo sau: want, decide, plan, hope, promise, agree, refuse, manage, afford",
      "Cả hai nhưng đổi nghĩa: remember/forget + to V (nhớ/quên làm gì - việc chưa xảy ra) vs + V-ing (nhớ/quên đã làm gì - việc đã xảy ra); stop + to V (dừng lại để làm gì khác) vs + V-ing (dừng hẳn việc đang làm)",
      "Sau giới từ luôn dùng V-ing: interested in doing, good at doing, before/after doing",
    ],
    examples: [
      { en: "I enjoy reading before bed.", vi: "Tôi thích đọc sách trước khi ngủ." },
      { en: "She decided to move to Da Nang.", vi: "Cô ấy quyết định chuyển đến Đà Nẵng." },
      { en: "I remember locking the door.", vi: "Tôi nhớ là đã khóa cửa (việc đã xảy ra)." },
      { en: "Please remember to lock the door.", vi: "Hãy nhớ khóa cửa nhé (việc chưa xảy ra)." },
      { en: "He stopped smoking last year.", vi: "Anh ấy đã bỏ hút thuốc năm ngoái." },
    ],
  },
  {
    id: "causative-form",
    categoryId: "verb-structures",
    title: "Causative Form (have/get something done)",
    level: "B2",
    summary: "Diễn tả việc nhờ/thuê người khác làm gì đó cho mình.",
    points: [
      "Công thức: S + have/get + O (vật) + V3/-ed",
      "'have something done' trang trọng/phổ biến hơn 'get something done'",
      "Khác với 'have someone do something' (S + have + O (người) + V nguyên thể) — nhờ một người cụ thể làm",
    ],
    examples: [
      { en: "I had my car repaired yesterday.", vi: "Hôm qua tôi đã mang xe đi sửa." },
      { en: "She got her hair cut last week.", vi: "Tuần trước cô ấy đã đi cắt tóc." },
      { en: "I'll have my assistant send you the file.", vi: "Tôi sẽ nhờ trợ lý của tôi gửi file cho bạn." },
    ],
  },
  {
    id: "question-tags",
    categoryId: "verb-structures",
    title: "Question Tags (Câu hỏi đuôi)",
    level: "A2",
    summary: "Thêm câu hỏi ngắn ở cuối câu để xác nhận thông tin.",
    points: [
      "Câu khẳng định → đuôi phủ định; câu phủ định → đuôi khẳng định",
      "Dùng trợ động từ tương ứng với thì của mệnh đề chính (do/does/did, is/are/was/were, have/has, will, can...)",
      "'I am' → tag là 'aren't I?'",
      "Câu mệnh lệnh thường dùng đuôi 'will you?'; 'Let's...' dùng đuôi 'shall we?'",
    ],
    examples: [
      { en: "You like coffee, don't you?", vi: "Bạn thích cà phê, phải không?" },
      { en: "She isn't coming, is she?", vi: "Cô ấy không đến, phải không?" },
      { en: "They have finished, haven't they?", vi: "Họ đã xong rồi, phải không?" },
      { en: "Let's go, shall we?", vi: "Đi thôi, được chứ?" },
    ],
  },

  // ───────────────────────── Nouns, articles, pronouns ─────────────────────────
  {
    id: "articles",
    categoryId: "nouns-articles-pronouns",
    title: "Articles (a / an / the / zero article)",
    level: "A1",
    summary: "Mạo từ xác định và không xác định đứng trước danh từ.",
    points: [
      "a/an: dùng khi nhắc đến danh từ đếm được số ít lần đầu, chưa xác định cụ thể (a trước phụ âm, an trước nguyên âm khi PHÁT ÂM)",
      "the: dùng khi cả người nói và người nghe đều biết rõ đối tượng được nhắc đến (đã nhắc trước đó, duy nhất, hoặc rõ ràng theo ngữ cảnh)",
      "Không dùng mạo từ (zero article): danh từ số nhiều/không đếm được mang nghĩa chung chung, tên riêng, tên quốc gia số ít, bữa ăn, môn thể thao",
    ],
    examples: [
      { en: "I saw a dog. The dog was barking loudly.", vi: "Tôi thấy một con chó. Con chó đó sủa rất to." },
      { en: "She is an engineer.", vi: "Cô ấy là kỹ sư." },
      { en: "The sun rises in the east.", vi: "Mặt trời mọc ở hướng đông (duy nhất)." },
      { en: "Cats are independent animals.", vi: "Mèo là loài vật độc lập (nói chung, không mạo từ)." },
    ],
  },
  {
    id: "countable-uncountable",
    categoryId: "nouns-articles-pronouns",
    title: "Countable & Uncountable Nouns",
    level: "A1",
    summary: "Danh từ đếm được (có số ít/nhiều) và danh từ không đếm được (chỉ có một dạng).",
    points: [
      "Danh từ đếm được: có dạng số ít/số nhiều (a book/books), dùng với a/an, many, few, number of",
      "Danh từ không đếm được: không có số nhiều, không dùng với a/an, dùng với much, little, amount of (nước, thông tin, tiền, đồ đạc, lời khuyên...)",
      "Lượng từ dùng chung cho cả hai: some, any, a lot of, plenty of",
      "Để đếm danh từ không đếm được: dùng đơn vị đo (a piece of information, a cup of coffee, a bottle of water)",
    ],
    examples: [
      { en: "I have three books.", vi: "Tôi có ba quyển sách." },
      { en: "There isn't much water left.", vi: "Không còn nhiều nước." },
      { en: "Can I have a piece of advice?", vi: "Cho tôi xin một lời khuyên được không?" },
      { en: "She doesn't have much furniture in her apartment.", vi: "Cô ấy không có nhiều đồ đạc trong căn hộ." },
    ],
  },
  {
    id: "plural-nouns",
    categoryId: "nouns-articles-pronouns",
    title: "Plural Noun Rules",
    level: "A1",
    summary: "Quy tắc thêm 's' để tạo danh từ số nhiều và các trường hợp bất quy tắc.",
    points: [
      "Quy tắc chung: thêm -s (books, cars)",
      "Danh từ tận cùng -s, -x, -ch, -sh, -o: thêm -es (buses, boxes, watches, potatoes)",
      "Danh từ tận cùng phụ âm + y: đổi y→i + es (city→cities); nguyên âm + y: chỉ thêm s (day→days)",
      "Danh từ tận cùng -f/-fe: đổi thành -ves (leaf→leaves, knife→knives) — một số ngoại lệ chỉ thêm s (roof→roofs)",
      "Bất quy tắc: man→men, woman→women, child→children, foot→feet, tooth→teeth, mouse→mice, person→people",
      "Không đổi dạng: sheep, fish, deer, series",
    ],
    examples: [
      { en: "There are three buses at the station.", vi: "Có ba chiếc xe buýt ở bến." },
      { en: "Two children are playing outside.", vi: "Hai đứa trẻ đang chơi bên ngoài." },
      { en: "I saw some sheep in the field.", vi: "Tôi thấy vài con cừu ở cánh đồng." },
    ],
  },
  {
    id: "pronouns",
    categoryId: "nouns-articles-pronouns",
    title: "Pronouns (Đại từ)",
    level: "A1",
    summary: "Đại từ nhân xưng, sở hữu, phản thân, bất định thay thế cho danh từ.",
    points: [
      "Đại từ chủ ngữ: I, you, he, she, it, we, they",
      "Đại từ tân ngữ: me, you, him, her, it, us, them",
      "Tính từ sở hữu: my, your, his, her, its, our, their + danh từ",
      "Đại từ sở hữu: mine, yours, his, hers, ours, theirs (đứng độc lập, không kèm danh từ)",
      "Đại từ phản thân: myself, yourself, himself, herself, itself, ourselves, yourselves, themselves",
      "Đại từ bất định: someone/somebody, anyone/anybody, no one/nobody, everyone/everybody, something, anything, nothing, everything",
    ],
    examples: [
      { en: "She gave the book to him.", vi: "Cô ấy đưa quyển sách cho anh ấy." },
      { en: "This bag is mine, not yours.", vi: "Cái túi này là của tôi, không phải của bạn." },
      { en: "He hurt himself while playing football.", vi: "Anh ấy tự làm mình bị thương khi chơi bóng đá." },
      { en: "Someone left this here.", vi: "Ai đó đã để cái này ở đây." },
    ],
  },
  {
    id: "relative-clauses",
    categoryId: "nouns-articles-pronouns",
    title: "Relative Clauses (Mệnh đề quan hệ)",
    level: "B1",
    summary: "Mệnh đề bổ sung thông tin cho danh từ đứng trước, dùng who/which/that/whose/where/when.",
    points: [
      "who: thay cho người (làm chủ ngữ hoặc tân ngữ)",
      "which: thay cho vật/sự việc",
      "that: thay cho cả người và vật, dùng trong mệnh đề quan hệ xác định (không dùng sau dấu phẩy)",
      "whose: chỉ sở hữu (của người/vật)",
      "where: thay cho nơi chốn; when: thay cho thời gian",
      "Mệnh đề xác định (không dấu phẩy) — thông tin cần thiết để xác định danh từ",
      "Mệnh đề không xác định (có dấu phẩy) — thông tin bổ sung, có thể lược bỏ mà câu vẫn đủ nghĩa; KHÔNG dùng 'that' trong loại này",
    ],
    examples: [
      { en: "The man who called you is my brother.", vi: "Người đàn ông gọi cho bạn là anh trai tôi." },
      { en: "This is the book which I told you about.", vi: "Đây là quyển sách mà tôi đã kể cho bạn." },
      { en: "The house whose roof is red belongs to my uncle.", vi: "Ngôi nhà có mái màu đỏ là của chú tôi." },
      { en: "My sister, who lives in Hanoi, is visiting us next week.", vi: "Chị tôi, người sống ở Hà Nội, sẽ đến thăm chúng tôi tuần sau." },
    ],
  },

  // ───────────────────────── Adjectives & adverbs ─────────────────────────
  {
    id: "comparatives-superlatives",
    categoryId: "adjectives-adverbs",
    title: "Comparatives & Superlatives",
    level: "A2",
    summary: "So sánh hơn và so sánh nhất giữa các sự vật/sự việc.",
    points: [
      "Tính từ ngắn (1 âm tiết): thêm -er/-est (tall→taller→the tallest)",
      "Tính từ ngắn tận cùng phụ âm-nguyên âm-phụ âm: gấp đôi phụ âm cuối (big→bigger→the biggest)",
      "Tính từ dài (2+ âm tiết): more/most + tính từ (beautiful→more beautiful→the most beautiful)",
      "Bất quy tắc: good→better→the best, bad→worse→the worst, far→farther/further→the farthest/furthest",
      "So sánh bằng: as + adj + as; so sánh kém: less + adj + than",
      "Cấu trúc nhấn mạnh: much/a lot/far + comparative (much better)",
    ],
    examples: [
      { en: "She is taller than her brother.", vi: "Cô ấy cao hơn anh trai mình." },
      { en: "This is the most interesting book I've ever read.", vi: "Đây là quyển sách thú vị nhất tôi từng đọc." },
      { en: "My cooking is not as good as my mom's.", vi: "Nấu ăn của tôi không ngon bằng mẹ tôi." },
      { en: "This laptop is much faster than my old one.", vi: "Laptop này nhanh hơn nhiều so với cái cũ của tôi." },
    ],
  },
  {
    id: "adjective-order",
    categoryId: "adjectives-adverbs",
    title: "Adjective Order (Thứ tự tính từ)",
    level: "B1",
    summary: "Khi nhiều tính từ cùng bổ nghĩa cho một danh từ, chúng theo một trật tự nhất định.",
    points: [
      "Trật tự chuẩn: Opinion (ý kiến) → Size (kích thước) → Age (tuổi) → Shape (hình dạng) → Color (màu sắc) → Origin (nguồn gốc) → Material (chất liệu) → Purpose (mục đích) + Noun",
      "Ví dụ ghi nhớ: a lovely (opinion) small (size) old (age) round (shape) red (color) Vietnamese (origin) wooden (material) dining (purpose) table",
    ],
    examples: [
      { en: "She bought a beautiful small antique wooden box.", vi: "Cô ấy đã mua một chiếc hộp gỗ cổ nhỏ xinh đẹp." },
      { en: "He wore a black leather jacket.", vi: "Anh ấy mặc một chiếc áo khoác da màu đen." },
    ],
  },
  {
    id: "so-such-too-enough",
    categoryId: "adjectives-adverbs",
    title: "So/Such, Too/Enough",
    level: "B1",
    summary: "Cấu trúc nhấn mạnh và diễn tả mức độ đủ/quá mức.",
    points: [
      "so + adj/adv (+ that): so tired (that)",
      "such + a/an + adj + noun (+ that): such a good idea (that)",
      "too + adj/adv (+ to V): quá... để làm gì (mang nghĩa tiêu cực, không thể)",
      "adj/adv + enough (+ to V): đủ... để làm gì",
    ],
    examples: [
      { en: "It was so cold that we stayed indoors.", vi: "Trời lạnh đến mức chúng tôi phải ở trong nhà." },
      { en: "It was such a cold day that we stayed indoors.", vi: "Đó là một ngày lạnh đến mức chúng tôi phải ở trong nhà." },
      { en: "This coffee is too hot to drink.", vi: "Cà phê này quá nóng để uống." },
      { en: "She isn't old enough to drive.", vi: "Cô bé chưa đủ tuổi để lái xe." },
    ],
  },
  {
    id: "adverbs",
    categoryId: "adjectives-adverbs",
    title: "Adverbs (Trạng từ)",
    level: "A2",
    summary: "Trạng từ bổ nghĩa cho động từ, tính từ, hoặc cả câu — chỉ cách thức, mức độ, tần suất, thời gian.",
    points: [
      "Trạng từ cách thức: thường thêm -ly vào tính từ (quick→quickly, careful→carefully)",
      "Tính từ bất quy tắc khi thành trạng từ: good→well, fast→fast, hard→hard",
      "Trạng từ tần suất (always, usually, often, sometimes, rarely, never): đứng TRƯỚC động từ thường, SAU 'to be'",
      "Trạng từ chỉ mức độ: very, quite, extremely, fairly + tính từ/trạng từ khác",
      "Vị trí trạng từ chỉ thời gian/nơi chốn: thường đứng cuối câu",
    ],
    examples: [
      { en: "She speaks English fluently.", vi: "Cô ấy nói tiếng Anh trôi chảy." },
      { en: "He is always late for meetings.", vi: "Anh ấy luôn trễ giờ họp." },
      { en: "I usually go jogging in the morning.", vi: "Tôi thường chạy bộ vào buổi sáng." },
      { en: "This exercise is quite difficult.", vi: "Bài tập này khá khó." },
    ],
  },

  // ───────────────────────── Prepositions & conjunctions ─────────────────────────
  {
    id: "prepositions-time",
    categoryId: "prepositions-conjunctions",
    title: "Prepositions of Time (in / on / at)",
    level: "A1",
    summary: "Giới từ chỉ thời gian: in (khoảng thời gian dài), on (ngày), at (giờ/thời điểm cụ thể).",
    points: [
      "at: giờ giấc cụ thể, thời điểm (at 7 p.m., at night, at the weekend - Anh Anh, at Christmas)",
      "on: ngày trong tuần, ngày cụ thể, ngày lễ có từ 'day' (on Monday, on July 4th, on my birthday)",
      "in: tháng, năm, mùa, thế kỷ, buổi trong ngày (in July, in 2024, in summer, in the morning)",
      "Không dùng giới từ trước: next/last/this/every + thời gian (next week, last year)",
    ],
    examples: [
      { en: "I wake up at 6 a.m.", vi: "Tôi thức dậy lúc 6 giờ sáng." },
      { en: "We're meeting on Friday.", vi: "Chúng ta sẽ gặp nhau vào thứ Sáu." },
      { en: "She was born in 1998.", vi: "Cô ấy sinh năm 1998." },
      { en: "I'll see you next week.", vi: "Hẹn gặp bạn tuần sau." },
    ],
  },
  {
    id: "prepositions-place",
    categoryId: "prepositions-conjunctions",
    title: "Prepositions of Place & Movement",
    level: "A1",
    summary: "Giới từ chỉ vị trí (in/on/at) và chuyển động (to/into/onto/through...).",
    points: [
      "in: bên trong không gian kín (in the room, in the box, in a city/country)",
      "on: trên bề mặt (on the table, on the wall, on the second floor)",
      "at: một điểm/địa chỉ cụ thể (at the door, at the bus stop, at 123 Main Street)",
      "to: hướng đến đích (go to school); into: đi vào bên trong (walk into the room)",
      "under/below, above/over, behind, in front of, between, among, next to/beside",
    ],
    examples: [
      { en: "The keys are on the table.", vi: "Chìa khóa ở trên bàn." },
      { en: "She is waiting at the bus stop.", vi: "Cô ấy đang đợi ở trạm xe buýt." },
      { en: "The cat ran into the room.", vi: "Con mèo chạy vào phòng." },
      { en: "The bank is between the pharmacy and the bakery.", vi: "Ngân hàng nằm giữa nhà thuốc và tiệm bánh." },
    ],
  },
  {
    id: "conjunctions",
    categoryId: "prepositions-conjunctions",
    title: "Conjunctions (Liên từ)",
    level: "A2",
    summary: "Liên từ kết hợp mệnh đề: liên từ kết hợp (FANBOYS) và liên từ phụ thuộc.",
    points: [
      "Liên từ kết hợp (nối hai mệnh đề độc lập, có dấu phẩy trước khi nối câu dài): for, and, nor, but, or, yet, so (FANBOYS)",
      "Liên từ phụ thuộc (bắt đầu mệnh đề phụ thuộc): because, although/though, since, while, if, unless, when, as soon as, even though",
      "'because' nêu lý do trực tiếp; 'although/though' diễn tả sự tương phản/nhượng bộ",
      "'unless' = 'if...not'",
    ],
    examples: [
      { en: "I wanted to go, but it was raining.", vi: "Tôi muốn đi, nhưng trời lại mưa." },
      { en: "Although she was tired, she kept working.", vi: "Mặc dù mệt, cô ấy vẫn tiếp tục làm việc." },
      { en: "I'll stay home unless you need me.", vi: "Tôi sẽ ở nhà trừ khi bạn cần tôi." },
      { en: "She left because she felt sick.", vi: "Cô ấy về vì cảm thấy không khỏe." },
    ],
  },

  // ───────────────────────── Questions & negatives ─────────────────────────
  {
    id: "yes-no-questions",
    categoryId: "questions-negatives",
    title: "Yes/No Questions",
    level: "A1",
    summary: "Câu hỏi trả lời bằng Yes/No, đảo trợ động từ lên trước chủ ngữ.",
    points: [
      "Có trợ động từ (be, have, will, can...): đảo trợ động từ lên trước chủ ngữ (Is she...? Have you...? Can they...?)",
      "Động từ thường ở Present/Past Simple: thêm do/does/did trước chủ ngữ + V nguyên thể (Do you like...? Did he go...?)",
      "Trả lời ngắn: Yes, S + trợ động từ. / No, S + trợ động từ + not.",
    ],
    examples: [
      { en: "Do you speak English? — Yes, I do.", vi: "Bạn có nói tiếng Anh không? — Có, tôi có." },
      { en: "Is she coming to the party? — No, she isn't.", vi: "Cô ấy có đến bữa tiệc không? — Không, cô ấy không đến." },
      { en: "Can you swim? — Yes, I can.", vi: "Bạn có biết bơi không? — Có, tôi biết." },
    ],
  },
  {
    id: "wh-questions",
    categoryId: "questions-negatives",
    title: "Wh- Questions",
    level: "A1",
    summary: "Câu hỏi lấy thông tin với What, Where, When, Who, Why, How, Which, Whose.",
    points: [
      "Công thức: Wh-word + trợ động từ + S + V...?",
      "What: cái gì; Where: ở đâu; When: khi nào; Who: ai; Why: tại sao; How: như thế nào; Which: cái nào; Whose: của ai",
      "Khi Who/What làm CHỦ NGỮ của câu hỏi thì KHÔNG đảo ngữ: Who called you? (không phải Who did call you?)",
      "How kết hợp: how much (giá/số lượng không đếm được), how many (số lượng đếm được), how long (bao lâu), how often (bao lâu một lần)",
    ],
    examples: [
      { en: "Where do you live?", vi: "Bạn sống ở đâu?" },
      { en: "Why were you late?", vi: "Sao bạn lại đến trễ?" },
      { en: "Who broke the window?", vi: "Ai đã làm vỡ cửa sổ? (Who là chủ ngữ)" },
      { en: "How often do you exercise?", vi: "Bạn tập thể dục thường xuyên như thế nào?" },
    ],
  },
  {
    id: "negation",
    categoryId: "questions-negatives",
    title: "Negative Sentences (Câu phủ định)",
    level: "A1",
    summary: "Cách tạo câu phủ định với trợ động từ + not.",
    points: [
      "Với 'to be': S + am/is/are + not",
      "Với động từ thường (Present/Past Simple): S + do/does/did + not + V (nguyên thể)",
      "Với các thì khác: thêm 'not' sau trợ động từ đầu tiên (haven't, won't, can't, wouldn't)",
      "Dạng viết tắt phổ biến: don't, doesn't, didn't, isn't, aren't, wasn't, weren't, haven't, hasn't, won't, can't",
      "Phủ định kép cần tránh: KHÔNG dùng 'don't...no/nothing/nobody' cùng lúc trong tiếng Anh chuẩn",
    ],
    examples: [
      { en: "I don't like spicy food.", vi: "Tôi không thích đồ ăn cay." },
      { en: "She isn't ready yet.", vi: "Cô ấy vẫn chưa sẵn sàng." },
      { en: "They haven't arrived yet.", vi: "Họ vẫn chưa đến." },
      { en: "I have nothing to say. (not: I don't have nothing to say)", vi: "Tôi không có gì để nói." },
    ],
  },
];
